/** Recherche ⌘K (barre latérale, `CommandPalette`) : vues, unités du dispositif au temps affiché, dossiers, camps
 *  et localités, fiches d'aide et glossaire — comme la recherche globale de Prevail. Fonction pure : les sources
 *  sont déjà traduites ; l'appelant exécute l'action du résultat choisi. Recherche tolérante aux accents et à la
 *  casse (`fold`). */

import type { HelpScreen } from "../help/topics";
import { fold } from "../help/topics";
import type { ObjectsFilter, ViewKey } from "../state/ui";

export { glossaryAnchor } from "../help/glossary";

export type CommandGroup = "views" | "units" | "dossiers" | "places" | "help" | "glossary";

/** Ordre des groupes dans la liste. */
export const COMMAND_GROUPS: readonly CommandGroup[] = ["views", "units", "dossiers", "places", "help", "glossary"];

export type CommandAction =
  | { type: "view"; view: ViewKey; objects?: ObjectsFilter }
  | { type: "unit"; code: string; lngLat: [number, number] }
  | { type: "dossier"; id: string; mapSlug: string | null }
  | { type: "camp"; slug: string }
  | { type: "locality"; lngLat: [number, number] }
  | { type: "topic"; id: string; screen: HelpScreen }
  | { type: "help"; tab: "all" | "glossary" }
  | { type: "onboarding" }
  | { type: "new-odb" }
  | { type: "glossary"; term: string };

export interface CommandResult {
  id: string;
  group: CommandGroup;
  label: string;
  detail?: string;
  action: CommandAction;
}

export interface CommandSources {
  views: { key: ViewKey; label: string; objects?: ObjectsFilter }[];
  units: { code: string; label: string; detail: string; lngLat: [number, number] }[];
  dossiers: { id: string; name: string; detail: string; mapSlug: string | null; text: string }[];
  camps: { slug: string; name: string; detail: string }[];
  localities: { key: string; name: string; detail: string; lngLat: [number, number] }[];
  topics: { id: string; screen: HelpScreen; title: string; detail: string; matches: (query: string) => boolean }[];
  /** Actions rangées avec les vues (« Nouvel ordre de bataille ») : cherchées dès deux caractères. */
  viewActions?: { id: string; label: string; keywords: string; action: CommandAction }[];
  /** Entrées fixes de l'aide (toute l'aide, glossaire, prise en main) : libellé et mots-clés cherchés. */
  helpActions: { id: string; label: string; keywords: string; action: CommandAction }[];
  glossary: { term: string; definition: string }[];
}

/** Nombre de résultats au plus par groupe. Lieux : les camps sont peu nombreux et tous listés, la borne ne vaut que
 *  pour les localités (`LOCALITY_LIMIT`, celle de la recherche des lieux d'origine). */
export const COMMAND_LIMITS: Record<CommandGroup, number> = {
  views: Infinity,
  units: 8,
  dossiers: 6,
  places: Infinity,
  help: 6,
  glossary: 6,
};
export const LOCALITY_LIMIT = 40;

/** Résultats pour `query`, groupés dans l'ordre de `COMMAND_GROUPS`. Sans texte : les vues et les camps. Les autres
 *  sources attendent deux caractères. */
export function searchCommands(query: string, src: CommandSources): CommandResult[] {
  const q = fold(query.trim());
  const long = q.length >= 2;
  const has = (text: string) => fold(text).includes(q);
  const out: Record<CommandGroup, CommandResult[]> = { views: [], units: [], dossiers: [], places: [], help: [], glossary: [] };
  const push = (result: CommandResult) => {
    const list = out[result.group];
    if (list.length < COMMAND_LIMITS[result.group]) list.push(result);
  };

  for (const view of src.views)
    if (!q || has(view.label))
      push({ id: `view-${view.key}-${view.objects ?? ""}`, group: "views", label: view.label, action: { type: "view", view: view.key, objects: view.objects } });

  if (long)
    for (const entry of src.viewActions ?? [])
      if (has(`${entry.label} ${entry.keywords}`)) push({ id: `action-${entry.id}`, group: "views", label: entry.label, action: entry.action });

  if (long) {
    for (const unit of src.units)
      if (has(`${unit.label} ${unit.code} ${unit.detail}`))
        push({ id: `unit-${unit.code}`, group: "units", label: unit.label, detail: unit.detail, action: { type: "unit", code: unit.code, lngLat: unit.lngLat } });
    for (const dossier of src.dossiers)
      if (dossier.text.includes(q))
        push({ id: `dossier-${dossier.id}`, group: "dossiers", label: dossier.name, detail: dossier.detail, action: { type: "dossier", id: dossier.id, mapSlug: dossier.mapSlug } });
  }

  for (const camp of src.camps)
    if (!q || has(camp.name)) push({ id: `camp-${camp.slug}`, group: "places", label: camp.name, detail: camp.detail, action: { type: "camp", slug: camp.slug } });
  if (long) {
    const seen = new Set<string>();
    for (const locality of src.localities) {
      if (seen.size >= LOCALITY_LIMIT) break;
      if (seen.has(locality.name) || !has(locality.name)) continue;
      seen.add(locality.name);
      push({ id: `loc-${locality.key}`, group: "places", label: locality.name, detail: locality.detail, action: { type: "locality", lngLat: locality.lngLat } });
    }

    for (const entry of src.helpActions)
      if (has(`${entry.label} ${entry.keywords}`)) push({ id: `help-${entry.id}`, group: "help", label: entry.label, action: entry.action });
    for (const topic of src.topics)
      if (topic.matches(query)) push({ id: `topic-${topic.id}`, group: "help", label: topic.title, detail: topic.detail, action: { type: "topic", id: topic.id, screen: topic.screen } });

    for (const entry of src.glossary)
      if (has(entry.term) || (q.length >= 3 && has(entry.definition)))
        push({ id: `glossary-${entry.term}`, group: "glossary", label: entry.term, detail: entry.definition, action: { type: "glossary", term: entry.term } });
  }

  return COMMAND_GROUPS.flatMap((group) => out[group]);
}
